/**
 * IAM-10 — every mutating browser call to the backend carries X-CSRF-Token.
 *
 * `apiClient` adds the header in its axios interceptor, but about 170 call
 * sites use a raw `fetch('/backend-api/…', { method: 'POST' | 'PUT' | 'PATCH' |
 * 'DELETE', credentials: 'include' })` without it. On the product paths the
 * backend still only LOGS those (`csrf_would_block`, CSRF_PRODUCT_ENFORCE
 * unset); the day it enforces, each of them would fail with a 403 — several
 * silently (the MCP tool-selection PATCH is debounced and shows no error).
 *
 * `installCsrfFetch` wraps `window.fetch` once so a same-origin request to
 * `/backend-api/` with a mutating method gets the current session's token
 * when the caller did not set one. It never touches other origins, other
 * paths, reads, or a header the caller already set.
 */

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const HEADER = 'X-CSRF-Token';

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return (input as Request).url ?? '';
}

function isBackendCall(url: string, origin: string | null): boolean {
  if (!url) return false;
  if (url.startsWith('/backend-api/')) return true;
  if (!origin) return false;
  return url.startsWith(`${origin}/backend-api/`);
}

/**
 * The `init` to send: unchanged unless this is a same-origin mutating backend
 * call without a CSRF header and a token exists, in which case the header is
 * added (the Request's own headers are kept).
 */
export function withCsrfHeader(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  token: string | null,
  origin: string | null,
): RequestInit | undefined {
  if (!token) return init;
  const req = typeof input === 'object' && !(input instanceof URL) ? (input as Request) : null;
  const method = String(init?.method ?? req?.method ?? 'GET').toUpperCase();
  if (!MUTATING.has(method)) return init;
  if (!isBackendCall(requestUrl(input), origin)) return init;
  const headers = new Headers(init?.headers ?? req?.headers ?? undefined);
  if (headers.has(HEADER)) return init;
  headers.set(HEADER, token);
  return { ...(init ?? {}), headers };
}

const INSTALLED = '__uzoCsrfFetchInstalled';

export function installCsrfFetch(getToken: () => string | null): void {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  const w = window as unknown as Record<string, unknown>;
  if (w[INSTALLED]) return;
  w[INSTALLED] = true;
  const original = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    let next = init;
    try {
      next = withCsrfHeader(input, init, getToken(), window.location?.origin ?? null);
    } catch {
      next = init; // never let the header logic break a request
    }
    return original(input, next);
  };
}
